import React from "react";
import PropTypes from "prop-types";
import { Box, Typography, TextField, InputAdornment } from "@mui/material";
import { format, parse, isValid } from "date-fns";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { capitalizeFirstLetter } from "../../../utils/capitalize";

const parseDateString = (dateStr) => {
  if (!dateStr) return null;
  if (dateStr instanceof Date) return dateStr;
  
  // Try yyyy-MM-dd
  let parsed = parse(dateStr, "yyyy-MM-dd", new Date());
  if (isValid(parsed)) return parsed;
  
  // Try MM/dd/yyyy
  parsed = parse(dateStr, "MM/dd/yyyy", new Date());
  if (isValid(parsed)) return parsed;
  
  // Try new Date
  const fallback = new Date(dateStr);
  if (isValid(fallback)) return fallback;
  
  return null;
};

const textFieldSx = (disabled) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: disabled ? "#F2F2F2" : "#FFFFFF",
    borderRadius: "8px",
    height: "45px",
     mt: 0.5,
    "& fieldset": { border: disabled ? "none" : "1px solid #E8E8E9" }
  },
  "& .MuiInputBase-input": { fontSize: "14px", color: disabled ? "#999893" : "#0e1324", pr: 0 },
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: "#999893",
    fontSize: "14px"
  }
});

const labelSx = {
  fontSize: "13px",
  color: "#66638C",
  fontWeight: 500,
  mb: 0.5,
  display: "block"
};

const columnBoxSx = (gap) => ({
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap,
});

const displayValue = (disabled, value) =>
  disabled && !value ? "-" : value || "";

const errorProps = (disabled, error) => ({
  error: disabled ? false : !!error,
  helperText: disabled ? undefined : error,
});

const counterSlotProps = (disabled, value, max) =>
  disabled
    ? undefined
    : {
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <Typography
                component="span"
                sx={{
                  fontSize: "12px",
                  color: "#666",
                  whiteSpace: "nowrap",
                  mr: 0.5,
                }}
              >
                {(value || "").length}/{max}
              </Typography>
            </InputAdornment>
          ),
        },
      };

const getGroupNameDisplayValue = (disabled, groupName) => {
  if (disabled) {
    return groupName ? capitalizeFirstLetter(groupName) : "-";
  }
  return groupName || "";
};

const BenefitsCoordination = ({ data, onChange, disabled, errors = {} }) => {
  const limitedChange = (key, max) => (e) => {
    if (e.target.value.length <= max) onChange(key, e.target.value);
  };

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography sx={{ fontWeight: 600, fontSize: "16px", color: "#403C6F", mb: 1 }}>
        Benefits Coordination
      </Typography>

      <Box sx={{ display: "flex", gap: 2 }}>
        <Box sx={columnBoxSx(0)}>
          <Typography sx={labelSx}>BIN (IIN Number)</Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter BIN"
            value={displayValue(disabled, data.iinNumber)}
            disabled={disabled}
            onChange={limitedChange("iinNumber", 8)}
            slotProps={counterSlotProps(disabled, data.iinNumber, 8)}
            sx={textFieldSx(disabled)}
          />
        </Box>
        <Box sx={columnBoxSx(0)}>
          <Typography sx={labelSx}>PCN (Processor ID)</Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter PCN"
            value={displayValue(disabled, data.processorIdentificationNumber)}
            disabled={disabled}
            onChange={limitedChange("processorIdentificationNumber", 15)}
            slotProps={counterSlotProps(
              disabled,
              data.processorIdentificationNumber,
              15,
            )}
            sx={textFieldSx(disabled)}
          />
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 2 }}>
        <Box sx={columnBoxSx(0)}>
          <Typography sx={labelSx}>
            Card Holder ID <span style={{ color: "#d32f2f" }}>*</span>
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter Card Holder ID"
            value={displayValue(disabled, data.cardHolderID)}
            disabled={disabled}
            {...errorProps(disabled, errors.cardHolderID)}
            onChange={limitedChange("cardHolderID", 35)}
            slotProps={counterSlotProps(disabled, data.cardHolderID, 35)}
            sx={textFieldSx(disabled)}
          />
        </Box>
        <Box sx={columnBoxSx(0)}>
          <Typography sx={labelSx}>
            Person Code <span style={{ color: "#d32f2f" }}>*</span>
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter Person Code"
            value={displayValue(disabled, data.personCode)}
            disabled={disabled}
            {...errorProps(disabled, errors.personCode)}
            onChange={limitedChange("personCode", 2)}
            slotProps={counterSlotProps(disabled, data.personCode, 2)}
            sx={textFieldSx(disabled)}
          />
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 2 }}>
        <Box sx={columnBoxSx(0)}>
          <Typography sx={labelSx}>
            Group ID <span style={{ color: "#d32f2f" }}>*</span>
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter Group ID"
            value={displayValue(disabled, data.groupID)}
            disabled={disabled}
            {...errorProps(disabled, errors.groupID)}
            onChange={limitedChange("groupID", 20)}
            slotProps={counterSlotProps(disabled, data.groupID, 20)}
            sx={textFieldSx(disabled)}
          />
        </Box>
        <Box sx={columnBoxSx(0)}>
          <Typography sx={labelSx}>
            Group Name <span style={{ color: "#d32f2f" }}>*</span>
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter Group Name"
            value={getGroupNameDisplayValue(disabled, data.groupName)}
            disabled={disabled}
            {...errorProps(disabled, errors.groupName)}
            onChange={limitedChange("groupName", 250)}
            slotProps={counterSlotProps(disabled, data.groupName, 250)}
            sx={textFieldSx(disabled)}
          />
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 2 }}>
        <Box sx={columnBoxSx(0.5)}>
          <Typography sx={labelSx}>Benefit Start Date</Typography>
          {disabled && !data.benefitStartDate ? (
            <TextField fullWidth size="small" value="-" disabled sx={{...textFieldSx(disabled), pb:1}} />
          ) : (
            <DesktopDatePicker
              value={parseDateString(data.benefitStartDate)}
              disabled={disabled}
              onChange={(newValue) => {
                const formattedDate = (newValue && isValid(newValue)) ? format(newValue, "yyyy-MM-dd") : "";
                onChange("benefitStartDate", formattedDate);
              }}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  placeholder: "MM/DD/YYYY",
                  sx: {
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: disabled ? "#F2F2F2" : "#FFFFFF",
                      borderRadius: "8px",
                      height: "50px",
                      "& fieldset": { border: disabled ? "none" : "1px solid #E8E8E9" },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "14px",
                      color: disabled ? "#999893" : "#0e1324",
                    },
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#999893",
                      fontSize: "14px"
                    }
                  },
                },
              }}
            />
          )}
        </Box>
        <Box sx={columnBoxSx(0.5)}>
          <Typography sx={labelSx}>Phone Number</Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter phone number"
            value={displayValue(disabled, data.phone)}
            disabled={disabled}
            {...errorProps(disabled, errors.phone)}
            onChange={(e) => {
              const sanitized = e.target.value
                .replaceAll(/\D/g, "")
                .slice(0, 10);
              onChange("phone", sanitized);
            }}
            slotProps={counterSlotProps(disabled, data.phone, 10)}
            sx={textFieldSx(disabled)}
          />
        </Box>
      </Box>

    </Box>
  );
};

BenefitsCoordination.propTypes = {
  data: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  errors: PropTypes.object,
};

export default BenefitsCoordination;

